<template>
    <span class="print-documents">
        <b-button block :disabled="!canPrintDocuments" variant="primary"
            @click="isModalOpen = true; $emit('modal-opened')">{{ modalType }}</b-button>

        <b-modal v-model="isMailModalOpen" :cancel-disabled="true" :hide-footer="true"
            :ok-disabled="true"
            size="lg"
            title="E-Mail versenden">
            <template v-if="documentMailSelectedPath && documentMailSelected" v-slot:default="{ visible }">
                <div v-if="visible" class="print-documents-modal-content">
                    <csp-record-detail-document-mail
                        ref="documentMail"
                        :mail-document="documentMailSelected"
                        :path="documentMailSelectedPath"
                        @cancel-mail="cancelMail"
                        @close-mail-modal="isMailModalOpen = false"
                        @mail-sent="$emit('document-generated')"
                        @mail-validation-error="handleMailValidationError"/>
                </div>
            </template>
        </b-modal>

        <b-modal v-model="isModalOpen" size="lg" :title="modalType">
            <template v-slot:default="{ visible }">
                <div v-if="visible" class="print-documents-modal-content">

                    <div v-if="!documentsLoaded" class="loading-content-container d-flex justify-content-center">
                        <csp-loading-indicator/>
                    </div>

                    <div v-if="documentsLoaded">
                        <div v-for="(documents, groupKey) in documentsPerGroup" :key="groupKey">
                            <h2>{{ groupName(groupKey) }}</h2>
                            <b-table class="mb-3" :fields="tableFields" :items="documents" :striped="true">
                                <template v-slot:table-colgroup="scope">
                                    <col
                                        v-for="field in scope.fields"
                                        :key="field.key"
                                        :style="{ width: field.width ?? 'auto' }">
                                </template>
                                <template v-slot:cell(name)="data">
                                    <span>{{ data.item.name }}</span>
                                    <i v-if="data.item.labelExtension" style="white-space: normal">
                                        <br>
                                        {{ data.item.labelExtension }}
                                    </i>
                                    <div v-if="data.item.childDocumentsSelectable" style="white-space: normal">
                                        <b-form-checkbox
                                            v-for="selectable in data.item.childDocumentsSelectable"
                                            :key="data.item.id + '_' + selectable.id"
                                            v-model="selectable.checked">
                                            {{ selectable.label }}
                                        </b-form-checkbox>
                                    </div>
                                </template>
                                <template v-slot:cell(actions)="data">
                                    <template v-for="(formatLabel, format) in data.item.formats">
                                        <b-button v-if="format !== 'mail'"
                                            :key="format"
                                            :href="getDocumentCreationPath(data.item, format)"
                                            size="sm"
                                            variant="primary">
                                            {{ formatLabel }}
                                        </b-button>
                                        <b-button v-if="format === 'mail'"
                                            :key="format"
                                            size="sm"
                                            variant="primary"
                                            @click="loadMailAndOpenMailModal(data.item, format)">
                                            {{ formatLabel }}
                                        </b-button>
                                    </template>

                                </template>
                            </b-table>
                        </div>
                        <div v-if="firmSpecificDocumentList.length > 0">
                            <h2>Firmenspezifische Dokumente</h2>
                            <b-table class="mb-3" :fields="tableFields" :items="firmSpecificDocumentList"
                                :striped="true">
                                <template v-slot:table-colgroup="scope">
                                    <col
                                        v-for="field in scope.fields"
                                        :key="field.key"
                                        :style="{ width: field.width ?? 'auto' }">
                                </template>
                                <template v-slot:cell(name)="data">
                                    <span>{{ data.item.attributes.name }}</span>
                                </template>
                                <template v-slot:cell(actions)="data">
                                    <b-button
                                        key="mail"
                                        size="sm"
                                        variant="primary"
                                        @click="loadMailAndOpenMailModalFirmSpecificDocument(data.item)">
                                        MAIL
                                    </b-button>
                                </template>
                            </b-table>
                        </div>
                    </div>
                </div>
            </template>

            <template v-slot:modal-footer="{ ok }">
                <b-button variant="secondary" @click="ok">Schließen</b-button>
            </template>
        </b-modal>
    </span>
</template>

<script>
    import DocumentMailModal from '@desktop/components/record/document/DocumentMailModal';
    import Container from '@services/Container';

    const container = Container.getInstance();
    const ajaxService = container.getAjaxService();
    const flashMessenger = container.getFlashMessenger();

    export default {
        components: {
            'csp-record-detail-document-mail': DocumentMailModal,
        },
        props: {
            modalType: {
                type: String,
                required: true,
            },
            documentList: {
                type: Array,
                required: true,
            },
            firmSpecificDocumentList: {
                type: Array,
                default: () => [],
            },
            documentsLoaded: {
                type: Boolean,
                required: true,
            },
            canPrintDocuments: {
                type: Boolean,
                default: true,
            },
        },
        emits: ['modal-opened', 'document-generated'],
        data() {
            return {
                isModalOpen: false,
                isMailModalOpen: false,
                tableFields: [
                    {key: 'name', label: 'Name', sortable: true},
                    {key: 'actions', label: 'Aktionen', width: '200px', class: 'text-right'},
                ],
                documentMailSelected: null,
                documentMailSelectedPath: null,
                sendMail: false,
            }
        },
        computed: {
            documentsPerGroup() {
                return Object.groupBy(this.documentList, ({group}) => group);
            },
        },
        methods: {
            getDocumentCreationPath(document, format) {
                // TODO Move to recordRepository and declare type for document
                const urlParameters = document.urlOptions;

                let urlPath = '/de';
                urlPath += '/' + urlParameters.module;
                urlPath += '/' + urlParameters.controller;
                urlPath += '/' + urlParameters.action;
                urlPath += '/sessionKey/' + urlParameters.sessionKey;
                urlPath += '/format/' + format;

                if (Array.isArray(document.childDocumentsSelectable)) {
                    document.childDocumentsSelectable.forEach((selectable) => {
                        const paramObj = {};
                        paramObj[selectable.id] = selectable.checked;

                        urlPath = ajaxService.appendParametersToUrl(
                            urlPath,
                            paramObj
                        );
                    });
                }

                return ajaxService.addSessionIdQueryParameter(urlPath);
            },
            groupName(groupKey) {
                if (groupKey === 'null') {
                    return 'Individuelle Dokumente';
                }
                return groupKey;
            },
            loadMailAndOpenMailModal(item, format) {
                this.documentMailSelected = item;
                this.documentMailSelectedPath = this.getDocumentCreationPath(item, format);
                this.isModalOpen = false;
                this.isMailModalOpen = true;
            },
            loadMailAndOpenMailModalFirmSpecificDocument(item) {
                const recordId = parseInt(this.$route.params.recordId);
                this.documentMailSelected = item;
                this.documentMailSelectedPath = '/de/Schaden-Manager/Akte/documentfirmspecific' +
                    '/vehicleDamageRecordId/' + recordId + '/step/firm-specific-mail' +
                    '/firmSpecificDocumentId/' + item.id;
                this.isModalOpen = false;
                this.isMailModalOpen = true;
            },
            cancelMail() {
                this.isMailModalOpen = false;
            },
            handleMailValidationError(errors) {
                this.isModalOpen = true;
                this.isMailModalOpen = false;
                flashMessenger.addError(
                    errors.join('<br />')
                )
            },
        },
    }
</script>
